import { Controller } from "@hotwired/stimulus";

// What is left of the registration form once the pickers became generic (SelectComponent +
// select_controller) and the city autocomplete became its own controller: a live
// koncertowo.com/<slug> preview under the name field. Pure enhancement - without JS the field
// still submits normally, it just doesn't preview.
export default class extends Controller {
  static targets = ["nameInput", "slugPreview", "submit"];

  connect() {
    this.updateSlugPreview();
    // Back to this form from the page it submitted to: the browser can restore the whole DOM
    // from the back/forward cache, disabled button and all, and `connect` does NOT run again
    // for that. Without this the form comes back dead - and this is the form the shortest way
    // into the service goes through (#133).
    this.unlock = this.unlock.bind(this);
    window.addEventListener("pageshow", this.unlock);
  }

  disconnect() {
    window.removeEventListener("pageshow", this.unlock);
  }

  // Registration is the one form in the app that submits WITHOUT Turbo: the session boundary
  // has to be a document navigation (see the comment in _registration_form). That also means
  // nothing else is left to stop a second click - no `turbo_submits_with`, and a submit in
  // flight looks exactly like one that has not started. A double-submitted registration is a
  // duplicate-address error at best, and on the invitation path it is an answer sent twice.
  //
  // The guard is the flag, not the attribute: Enter in a text field submits too, and a
  // `disabled` button does not stop that.
  lock(event) {
    if (this.locked) return event.preventDefault();

    this.locked = true;
    if (!this.hasSubmitTarget) return;

    // Disabling the submitter drops its own `commit` value from the payload - nothing reads
    // it - and the rest of the fields are untouched, so what the form carries does not change.
    this.submitTarget.disabled = true;
    const label = this.submitTarget.dataset.submittingLabel;
    if (label) this.submitTarget.value = label;
  }

  unlock(event) {
    if (event && !event.persisted) return;

    this.locked = false;
    if (!this.hasSubmitTarget) return;

    this.submitTarget.disabled = false;
    const label = this.submitTarget.dataset.idleLabel;
    if (label) this.submitTarget.value = label;
  }

  updateSlugPreview() {
    if (!this.hasNameInputTarget || !this.hasSlugPreviewTarget) return;

    const slug = this.slugify(this.nameInputTarget.value);
    this.slugPreviewTarget.textContent = `koncertowo.com/${slug || "..."}`;
  }

  // Same transliteration Profile#generate_slug relies on (Rails' `parameterize` and its default
  // ASCII approximation table - no locale-specific rule is configured). Only the Polish diacritics
  // actually usable in a profile name are mapped; the rest follows the same "strip to [a-z0-9-]".
  slugify(value) {
    const plMap = {
      ą: "a", ć: "c", ę: "e", ł: "l", ń: "n", ó: "o", ś: "s", ź: "z", ż: "z",
    };

    return value
      .toLowerCase()
      .replace(/[ąćęłńóśźż]/g, (ch) => plMap[ch])
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }
}
